import { Medication } from '../../../dtos/patientmr/medication.dto';
import { VisitStatus } from '../../../dtos/patientmr/visit.dto';
import type { ChartEntryInvalidationInfo, ChartAmendment, ChartEntryVersion, ChartAddendumInfo, MarStatus, NoteCategory, EntryVisibility } from '../../../dtos/patientmr/charting';

// Props for the main Charting component
export interface ChartingProps {
  expanded: boolean;
  toggleExpanded: () => void;
  patientId: string;
  activeVisitId?: string;
  /** Phase 16C: the active visit's window, for the "Observed at" default and bounds */
  visitWindow?: { startedAt?: string | null; endedAt?: string | null };
  /** Phase 16C: offline visit workspace (hides visit history and online-only actions) */
  workspace?: boolean;
  /** Phase 18 M3: 'DOCPENDING' when `activeVisitId` is the visit just ended ("Review and sign") */
  activeVisitStatus?: string;
  /** Phase 18 M3: the ended visit's chart was submitted (the host drops the review state) */
  onReviewDone?: () => void;
  /**
   * Phase 18U U1 (U-Q2): on a phone or tablet the patient page charts nothing itself; the
   * current visit shows an "Open current visit" card that calls this with the visit id.
   */
  onOpenVisit?: (visitId: string) => void;
  /**
   * Phase 18U U2: the visit screen's sticky action bar. Called with a function that opens the
   * Add sheet and whether a new entry can be added now (chart in draft, no form open).
   */
  renderActionBar?: (openAdd: () => void, canAdd: boolean) => React.ReactNode;
  /** Phase 18U U16: open this visit's chart for review (from the review queue) */
  openVisitId?: string;
  /** Called once `openVisitId` has been opened */
  onOpenVisitHandled?: () => void;
}

// Styles for different entry types
export const entryTypeStyles = {
  vitals: {
    bg: 'bg-blue-50',
    border: 'border-blue-800',
    iconColor: 'text-blue-700',
    headerBg: 'bg-blue-100/50'
  },
  mar: {
    bg: 'bg-green-50',
    border: 'border-green-800',
    iconColor: 'text-green-700',
    headerBg: 'bg-green-100/50'
  },
  feed: {
    bg: 'bg-amber-50',
    border: 'border-amber-800',
    iconColor: 'text-amber-700',
    headerBg: 'bg-amber-100/50'
  },
  notes: {
    bg: 'bg-purple-50',
    border: 'border-purple-800',
    iconColor: 'text-purple-700',
    headerBg: 'bg-purple-100/50'
  },
  wound: {
    bg: 'bg-pink-50',
    border: 'border-pink-800',
    iconColor: 'text-pink-700',
    headerBg: 'bg-pink-100/50'
  },
  incident: {
    bg: 'bg-red-50',
    border: 'border-red-800',
    iconColor: 'text-red-700',
    headerBg: 'bg-red-100/50'
  },
  summary: {
    bg: 'bg-slate-50',
    border: 'border-slate-800',
    iconColor: 'text-slate-700',
    headerBg: 'bg-slate-100/50'
  },
  measurements: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-800',
    iconColor: 'text-yellow-700',
    headerBg: 'bg-yellow-100/50'
  },
  addendum: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-800',
    iconColor: 'text-indigo-700',
    headerBg: 'bg-indigo-100/50'
  },
  respiratory: {
    bg: 'bg-cyan-50',
    border: 'border-cyan-800',
    iconColor: 'text-cyan-700',
    headerBg: 'bg-cyan-100/50'
  },
  seizure: {
    bg: 'bg-orange-50',
    border: 'border-orange-800',
    iconColor: 'text-orange-700',
    headerBg: 'bg-orange-100/50'
  },
  output: {
    bg: 'bg-lime-50',
    border: 'border-lime-800',
    iconColor: 'text-lime-700',
    headerBg: 'bg-lime-100/50'
  },
  supervisory: {
    bg: 'bg-teal-50',
    border: 'border-teal-800',
    iconColor: 'text-teal-700',
    headerBg: 'bg-teal-100/50'
  },
  // Phase 20D aide / attendant task note
  aidetasks: {
    bg: 'bg-lime-50',
    border: 'border-lime-800',
    iconColor: 'text-lime-700',
    headerBg: 'bg-lime-100/50'
  },
  // Phase 20C treatment note
  therapy: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-800',
    iconColor: 'text-emerald-700',
    headerBg: 'bg-emerald-100/50'
  }
};

export type EntryStyle = typeof entryTypeStyles.vitals;

/** Theme for a local entry type, with a neutral fallback */
export const styleForEntryType = (type: string | null | undefined): EntryStyle =>
  (type && (entryTypeStyles as Record<string, EntryStyle>)[type]) || entryTypeStyles.notes;

// Extended interface to handle both local Entry format and the API ChartEntry format
export interface Entry {
  id: number | string;
  type: string;
  date: string;
  provider: string;
  visitId?: string | number;
  data:
    | { temperature: string; bloodPressure: string; pulse: string; respiratoryRate: string; o2Saturation: string; }
    | { height: string; heightUnit: string; weight: string; weightUnit: string; headCircumference: string; headCircumferenceUnit: string; }
    | string
    | any; // Added any to handle various data formats from API
  lastEdited?: string;
  flagged?: boolean;
  invalidated?: boolean;
  /** Who invalidated the entry, when and why (Phase 4) */
  invalidationInfo?: ChartEntryInvalidationInfo;
  /** Phase 9C: late entry, amendments, supervisor addendum, author */
  lateEntry?: boolean;
  minutesAfterEnd?: number | null;
  version?: number;
  amended?: boolean;
  lastAmendment?: ChartAmendment | null;
  versions?: ChartEntryVersion[];
  addendum?: ChartAddendumInfo | null;
  createdBy?: string;
  /** Phase 16C: observed / documented times and the device sync state */
  observedAt?: string;
  documentedAt?: string;
  syncState?: 'pending' | 'synced' | 'conflict' | 'needs_attention';
  /** 16B optimistic-concurrency counter (sent back as expectedVersion) */
  editVersion?: number;
}

// Interface for historical visits
export interface HistoricalVisit {
  id: number | string;
  date: string;
  type: string;
  status?: VisitStatus;
  /** CHT-08: worst incident severity charted on the visit */
  maxIncidentSeverity?: string;
  /** Phase 18U U15: chart status (DRAFT / SUBMITTED / QA / COMPLETE), clinician, discipline */
  chartingStatus?: string;
  providerName?: string;
  discipline?: string;
}

/** MAR picker value for a medication typed in by hand (server records notOnMedList: true, MED-03) */
export const NOT_ON_MED_LIST = '__not_on_med_list__';

// Form data interfaces. Numbers are kept as strings while typing; blank means "not measured"
// and is left out of the request (Phase 18 M1: never sent as 0).
export interface MarFormData {
  /** Picked medication id, NOT_ON_MED_LIST for free text, or '' when nothing is chosen */
  medicationId: string;
  medicationName: string;
  doseAmount: string;
  doseUnit: string;
  route: string;
  administrationTime: string; // HH:MM 24h format
  notes: string;
  /** Phase 18 M7 */
  status: MarStatus;
  /** Required unless GIVEN ('' when not chosen) */
  withholdReason: string;
  /** Required when the picked medication is PRN */
  indication: string;
  /** Follow-up of a PRN GIVEN entry: effectiveness + the entry id */
  effectiveness: string;
  followUpOf: string;
  site: string;
  reaction: string;
}

export interface IncidentFormData {
  incidentType: string;
  /** CHT-08: LOW | MODERATE | HIGH | CRITICAL, or '' when not set */
  severity: string;
  description: string;
  /** Phase 18 M6: the "Internal (staff only)" toggle; also sent as visibility INTERNAL */
  internalOnly: boolean;
  /** Phase 18 M8: fields the DTO already had */
  actionsTaken: string;
  reportedTo: string;
  /** Comma-separated names */
  witnesses: string;
  injuryDetails: string;
}

/** Structured pediatric feed (Phase 9C); numbers are kept as strings while typing */
export interface FeedFormData {
  feedType: string;
  formula: string;
  volumeMl: string;
  durationMinutes: string;
  tolerance: string;
  notes: string;
  /** Phase 18 M8 */
  rateMlPerHour: string;
  residualMl: string;
}

export interface VitalFormData {
  temperature: string;
  bloodPressure: string;
  bloodPressureSystolic: string;
  bloodPressureDiastolic: string;
  pulse: string;
  respiratoryRate: string;
  o2Saturation: string;
  /** Phase 18 M8: FLACC | FACES | NRS, or '' */
  painScale: string;
  painScore: string;
  o2FlowLpm: string;
}

/** Phase 19 F22: required when the note category is TEACHING */
export interface TeachingFormData {
  subject: string;
  learner: string;
  method: string;
  durationMinutes: string;
  understanding: string;
}

/** Phase 18 M6/M13: note text, category, visibility and the teaching record */
export interface NoteFormData {
  note: string;
  category: NoteCategory;
  visibility: EntryVisibility;
  teaching: TeachingFormData;
}

/** Phase 18 M8: wound care sends location, L x W x D, drainage and treatment */
export interface WoundFormData {
  woundLocation: string;
  woundDescription: string;
  drainage: string;
  treatmentPerformed: string;
  length: string;
  width: string;
  depth: string;
  units: string;
}

/** Phase 18 M8 RESPIRATORY */
export interface RespiratoryFormData {
  mode: string;
  ventMode: string;
  rate: string;
  tidalVolumeMl: string;
  pip: string;
  peep: string;
  fio2: string;
  o2FlowLpm: string;
  spo2: string;
  suctionCount: string;
  secretionAmount: string;
  secretionColor: string;
  tolerance: string;
  alarmsChecked: boolean;
  circuitChecked: boolean;
  notes: string;
}

/** Phase 18 M8 SEIZURE; the start clock time doubles as the observed time */
export interface SeizureFormData {
  /** HH:MM */
  startedTime: string;
  durationMinutes: string;
  durationSeconds: string;
  seizureType: string;
  description: string;
  interventions: string[];
  rescueMedEntryId: string;
  postIctalState: string;
  /** Comma-separated (e.g. "Mother, Dr. Lee") */
  notified: string;
  notes: string;
}

/** Phase 18 M8 OUTPUT */
export interface OutputFormData {
  outputType: string;
  volumeMl: string;
  diaperCount: string;
  characteristics: string;
  notes: string;
}

/** Phase 19 F21 SUPERVISORY_NOTE */
export interface SupervisoryFormData {
  supervisedUserId: string;
  supervisedDiscipline: string;
  /** '' until answered */
  staffPresent: '' | 'YES' | 'NO';
  pocFollowed: string;
  competenciesObserved: string[];
  clientSatisfaction: string;
  issues: string;
  actions: string;
  nextDueDate: string;
}

/** Phase 20C THERAPY_TREATMENT; numbers stay strings while typing */
export interface TherapyFormLine {
  code: string;
  minutes: string;
}

export interface TherapyFormGoal {
  goal: string;
  goalId: string;
  /** '' until picked */
  progress: string;
  note: string;
}

export interface TherapyFormData {
  evaluationCode: string;
  lines: TherapyFormLine[];
  interventions: string;
  goalsAddressed: TherapyFormGoal[];
  homeProgram: string;
  patientResponse: string;
  coTreatment: boolean;
  coTreatmentWith: string;
  telehealth: boolean;
}

/** Phase 20D AIDE_TASKS; numbers stay strings while typing, '' status until picked */
export interface AideTaskFormRow {
  task: string;
  status: string;
  minutes: string;
  note: string;
}

export interface AideTasksFormData {
  tasks: AideTaskFormRow[];
  observations: string;
  reportedToSupervisor: boolean;
}

export interface MeasurementsFormData {
  height: string;
  heightUnit: string;
  weight: string;
  weightUnit: string;
  headCircumference: string;
  headCircumferenceUnit: string;
}

// Props for EntryListItem component
export interface EntryListItemProps {
  /** Phase 18U U4: the entry just saved; that row is scrolled into view and highlighted */
  highlightEntryId?: string | number | null;
  entry: Entry;
  handleEditEntry: (entry: Entry) => void;
  handleFlagEntry: (id: string | number) => void;
  handleInvalidateEntry: (id: string | number) => void;
  /** Chart is not a DRAFT (submitted/QA/complete): hide edit and invalidate */
  locked?: boolean;
  /** The visit has ended (G17: replaces the hard-coded past-entry flag) */
  visitEnded?: boolean;
  inPlaceEditingEntryId: string | number | null;
  setInPlaceEditingEntryId: (id: string | number | null) => void;
  renderEntryContent: (entry: Entry) => React.ReactNode;
  renderSelectedForm: (entryId: string | number) => React.ReactNode;
  cancelInPlaceEdit: () => void;
  handleSaveInPlaceEdit: (id: string | number) => void;
  isMobile: boolean;
  saving?: boolean;
}

// Props for EntryForm component
export interface EntryFormProps {
  selectedEntryType: string | null;
  vitalFormData: VitalFormData;
  setVitalFormData: React.Dispatch<React.SetStateAction<VitalFormData>>;
  marFormData: MarFormData;
  setMarFormData: React.Dispatch<React.SetStateAction<MarFormData>>;
  feedFormData: FeedFormData;
  setFeedFormData: React.Dispatch<React.SetStateAction<FeedFormData>>;
  notesFormData: NoteFormData;
  setNotesFormData: React.Dispatch<React.SetStateAction<NoteFormData>>;
  woundFormData: WoundFormData;
  setWoundFormData: React.Dispatch<React.SetStateAction<WoundFormData>>;
  incidentFormData: IncidentFormData;
  setIncidentFormData: React.Dispatch<React.SetStateAction<IncidentFormData>>;
  summaryFormData: string;
  setSummaryFormData: React.Dispatch<React.SetStateAction<string>>;
  measurementsFormData: MeasurementsFormData;
  setMeasurementsFormData: React.Dispatch<React.SetStateAction<MeasurementsFormData>>;
  editingEntryId: string | number | null;
  cancelAddingEntry: () => void;
  handleSubmitNewEntry: () => void;
  isMobile: boolean;
  patientMedications?: Medication[];
  /** Phase 16C: "Observed at" clock time (MAR uses its administration time instead) */
  observedTime?: string;
  setObservedTime?: (value: string) => void;
  observedError?: string | null;
}

// Props for HistoricalVisitList component
export interface HistoricalVisitListProps {
  historicalVisits: HistoricalVisit[];
  handleExpandVisit: (visit: HistoricalVisit) => void;
  isMobile: boolean;
}
